import React, { useState } from 'react';

interface DeleteLicenseDialogProps {
  apiUrl: string;
  authToken: string;
  license: {
    id: string;
    product: string;
    clientName: string | null;
    validUntil: string;
  };
  onClose: () => void;
  onSuccess: () => void;
}

const DeleteLicenseDialog: React.FC<DeleteLicenseDialogProps> = ({ 
  apiUrl, 
  authToken, 
  license, 
  onClose, 
  onSuccess 
}) => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleDelete = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(
        `${apiUrl}/api/licenses/${encodeURIComponent(license.id)}/${encodeURIComponent(license.product)}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Basic ${authToken}`,
          },
        }
      );

      if (response.ok) {
        onSuccess();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete license');
      }
    } catch (err) {
      setError('Failed to delete license');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 className="text-xl font-bold mb-4 text-red-600">Delete License</h2>
        
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800 font-medium mb-2">
            Are you sure you want to delete this license? This action cannot be undone.
          </p>
        </div>

        <div className="mb-4 p-3 bg-gray-50 rounded-md">
          <p className="text-sm text-gray-600 mb-1">
            <span className="font-medium">Identifier:</span> {license.id}
          </p>
          <p className="text-sm text-gray-600 mb-1">
            <span className="font-medium">Product:</span> {license.product}
          </p>
          {license.clientName && (
            <p className="text-sm text-gray-600 mb-1">
              <span className="font-medium">Client:</span> {license.clientName}
            </p>
          )}
          <p className="text-sm text-gray-600">
            <span className="font-medium">Valid Until:</span>{' '}
            {new Date(license.validUntil).toLocaleString()}
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={loading}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            {loading ? 'Deleting...' : 'Delete License'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteLicenseDialog;

